import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box } from "@material-ui/core";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import { SalesOrderSortListField } from "@saleor/createOrder/urls";
import {
  SapListOfSaleOrderInput,
  useDownloadListOfSaleOrderExcelLazyQuery,
  useListOfSaleOrderAndExcelLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import { useLocalPaginationState } from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import BottomBar from "@saleor/listOfSalesOrder/components/BottomBar";
import OrderListFilter from "@saleor/listOfSalesOrder/components/OrderListFilter";
import OrderListTable from "@saleor/listOfSalesOrder/components/OrderListTable";
import {
  listOfSaleOrderTrackingUrl,
  salesOrderListUrl,
  salesOrderTrackingListUrl,
  SalesOrderUrlQueryParams
} from "@saleor/listOfSalesOrder/urls";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import {
  asSortParams,
  getSortParams,
  getSortUrlVariables
} from "@saleor/utils/sort";
import { parse as parseQs } from "qs";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface OrdersViewProps {
  params: SalesOrderUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  () => ({
    container: {
      marginBottom: "7rem"
    },
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }),
  { name: "OrdersView" }
);

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex}>
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

function camelToSnake(str) {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

const ListOfSalesOrder: React.FC<OrdersViewProps> = () => {
  const intl = useIntl();
  const isSaleOrderTracking = location.pathname?.includes(
    listOfSaleOrderTrackingUrl
  );
  const [
    { fullScreen, filterListSalesOrder },
    dispatchAppState
  ] = useAppState();
  // const [reRender, setReRender] = useState<boolean>(false);
  const qs = parseQs(location.search.substring(1));
  const params = asSortParams(
    qs,
    SalesOrderSortListField,
    SalesOrderSortListField.createDate
  );
  const navigate = useNavigator();
  const classes = useStyles({ fullScreen });
  const [firstRender, setFirstRender] = useState<boolean>(true);

  const { updateListSettings, settings } = useListSettings(
    ListViews.CUSTOMER_ORDER
  );

  const [
    ,
    // filterParams
    setFilterParams
  ] = useState<SapListOfSaleOrderInput>(null);

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.CUSTOMER_ORDER.rowNumber
    );
  }, []);

  const [valuesPaginationState] = useLocalPaginationState(settings?.rowNumber);

  const [
    getDataReportListOfSalesOrder,
    { loading, data }
  ] = useListOfSaleOrderAndExcelLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      // setReRender(!reRender);
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    },
    onError: () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    }
  });

  const queryVariables = React.useMemo(() => {
    const filter = {
      ...filterListSalesOrder,
      saleOrg: filterListSalesOrder?.saleOrg ?? "All",
      soldTo: filterListSalesOrder?.soldTo
        ? filterListSalesOrder?.soldTo?.map(item => item.slice(0, 10))
        : null,
      isOrderTracking: isSaleOrderTracking
    };

    if (params?.sort) {
      filter.sortByField = {
        sortField: camelToSnake(params.sort),
        sortType: params.asc ? "ASC" : "DESC"
      };
    }

    return { filter };
  }, [params]);

  // const { data: dataExcel } = useDownLoadListOfSaleOrderExcelQuery({
  //  displayLoader: true,
  //  variables: queryVariables
  // });
  const [
    getDataExcel,
    { data: dataExcel }
  ] = useDownloadListOfSaleOrderExcelLazyQuery({
    onCompleted: () => {
      onDownloadExcel();
    },
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (Object.keys(filterListSalesOrder || {})?.length) {
      dispatchAppState({
        payload: {
          value: true
        },
        type: "displayLoader"
      });
      setFirstRender(false);
      getDataReportListOfSalesOrder({
        variables: {
          filter: {
            ...filterListSalesOrder,
            saleOrg: filterListSalesOrder?.saleOrg ?? "All",
            soldTo: filterListSalesOrder?.soldTo
              ? filterListSalesOrder?.soldTo?.map(item => item.slice(0, 10))
              : null,
            isOrderTracking: isSaleOrderTracking
          }
        }
      });
    }
  }, []);

  const listOrder = useMemo(() => {
    if (
      !firstRender &&
      data?.listOfSaleOrderAndExcel?.listOfSaleOrderSap?.length
    ) {
      return data.listOfSaleOrderAndExcel?.listOfSaleOrderSap;
    }
    return [];
  }, [data?.listOfSaleOrderAndExcel?.listOfSaleOrderSap]);

  const onDownloadExcel = () => {
    const anchor_href =
      "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64," +
      dataExcel?.downloadListOfSaleOrderExcel?.excel;
    const exportLinkElement = document.createElement("a");

    exportLinkElement.hidden = true;
    exportLinkElement.download = isSaleOrderTracking
      ? "Sales_of_Order_Tracking.xlsx"
      : "sales_of_order.xlsx";
    exportLinkElement.href = anchor_href;
    exportLinkElement.target = "_self";
    document.body.appendChild(exportLinkElement);
    exportLinkElement.click();
    exportLinkElement.remove();
    dispatchAppState({
      payload: {
        value: false
      },
      type: "displayLoader"
    });
  };

  const downloadListSalesOrder = async () => {
    await getDataExcel({
      variables: queryVariables
    });
  };

  const handleChangeFilter = (filterObj: SapListOfSaleOrderInput) => {
    setFilterParams(filterObj);
    dispatchAppState({
      type: "setFilterListSalesOrder",
      payload: {
        filterListSalesOrder: filterObj
      }
    });

    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    setFirstRender(false);
    getDataReportListOfSalesOrder({
      variables: {
        ...valuesPaginationState,
        filter: {
          ...filterObj,
          saleOrg: filterObj?.saleOrg ?? "All",
          soldTo: filterObj.soldTo
            ? filterObj.soldTo.map(item => item.slice(0, 10))
            : null,
          materialGroup1:
            filterObj.materialGroup1 === "ALL" ? "" : filterObj.materialGroup1,
          isOrderTracking: isSaleOrderTracking
        }
      }
    });
  };

  const handleSort = async (field, id) => {
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });

    const urlParams = {
      ...params,
      ...getSortUrlVariables(field, { ...params, soldToId: id })
    };

    const url = isSaleOrderTracking
      ? salesOrderTrackingListUrl(urlParams)
      : salesOrderListUrl(urlParams);

    navigate(url, { replace: true });

    dispatchAppState({
      payload: {
        value: false
      },
      type: "displayLoader"
    });
  };

  const message = useMemo(() => {
    if (loading || (!loading && firstRender)) {
      return {
        icon: InfoIcon,
        message: intl.formatMessage({
          defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
        }),
        color: "#555555"
      };
    }
    if (!loading && !firstRender) {
      if (!listOrder.length) {
        return {
          icon: XCircle,
          message: intl.formatMessage({
            defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
          }),
          color: "#FF2600"
        };
      }
      return {
        icon: CheckCircle,
        message: intl.formatMessage({
          defaultMessage: "พบรายการที่เกี่ยวข้อง"
        }),
        color: "#28A745"
      };
    }

    return {
      icon: InfoIcon,
      message: intl.formatMessage({
        defaultMessage: "กรุณากรอกข้อมูลและกดค้นหา"
      }),
      color: "#555555"
    };
  }, [listOrder.length, loading, firstRender]);

  const handleResetForm = () => {
    setFirstRender(true);
  };

  const onDownloadExcelClick = () => {
    dispatchAppState({
      payload: {
        value: true
      },
      type: "displayLoader"
    });
    downloadListSalesOrder();
  };

  return (
    <>
      <Container className={classes.container}>
        <PageHeader
          className={classes.pageHeader}
          title={
            isSaleOrderTracking
              ? intl.formatMessage({
                  defaultMessage: "List of Sales Order - Order Tracking",
                  description: "label"
                })
              : intl.formatMessage({
                  defaultMessage: "List of Sales Order",
                  description: "label"
                })
          }
        ></PageHeader>
        <OrderListFilter
          message={<SectionMessage {...message} />}
          onResetForm={handleResetForm}
          handleChangeFilter={handleChangeFilter}
          listOrder={listOrder}
          shouldSetDefaultCreatedBy={isSaleOrderTracking}
          loading={loading}
        />
        {(listOrder?.length || loading) && !firstRender ? (
          <OrderListTable
            data={listOrder}
            settings={settings}
            onUpdateListSettings={updateListSettings}
            totalCount={listOrder.length}
            onSort={handleSort}
            sort={getSortParams(params)}
            isSaleOrderTracking={isSaleOrderTracking}
          />
        ) : (
          <></>
        )}
        <BottomBar
          dataCount={listOrder.length}
          onDownload={onDownloadExcelClick}
        />
      </Container>
    </>
  );
};

export default ListOfSalesOrder;
